export type LogLevel = "DEBUG" | "INFO" | "WARNING" | "ERROR" | "FATAL";

const LEVEL_ORDER: Record<LogLevel, number> = {
  DEBUG: 10,
  INFO: 20,
  WARNING: 30,
  ERROR: 40,
  FATAL: 50,
};

const SERVICE = "workplace-deployment-wizard";

function isProduction(): boolean {
  return import.meta.env.PROD;
}

function minLevel(): LogLevel {
  return isProduction() ? "WARNING" : "DEBUG";
}

function createTraceId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `ew-${Date.now()}`;
}

let traceId = createTraceId();

export function getTraceId(): string {
  return traceId;
}

export function resetTraceId(): string {
  traceId = createTraceId();
  return traceId;
}

interface LogFields {
  message: string;
  durationMs?: number;
  statusCode?: number;
  error?: string;
  [key: string]: unknown;
}

function sanitize(fields: LogFields): LogFields {
  const blocked = ["email", "phone", "password", "token", "name"];
  const clean: LogFields = { message: fields.message };
  for (const [key, value] of Object.entries(fields)) {
    if (key === "message") continue;
    if (blocked.includes(key.toLowerCase())) continue;
    clean[key] = value;
  }
  return clean;
}

export function log(level: LogLevel, fields: LogFields): void {
  if (LEVEL_ORDER[level] < LEVEL_ORDER[minLevel()]) return;

  const entry = {
    timestamp: new Date().toISOString(),
    level,
    service: SERVICE,
    traceId,
    ...sanitize(fields),
  };

  const serialized = JSON.stringify(entry);
  if (level === "ERROR" || level === "FATAL") {
    console.error(serialized);
    return;
  }
  if (level === "WARNING") {
    console.warn(serialized);
    return;
  }
  console.info(serialized);
}

export const logger = {
  debug: (fields: LogFields) => log("DEBUG", fields),
  info: (fields: LogFields) => log("INFO", fields),
  warning: (fields: LogFields) => log("WARNING", fields),
  error: (fields: LogFields) => log("ERROR", fields),
  fatal: (fields: LogFields) => log("FATAL", fields),
};
